import { ExportIcon } from "@xola/icons";
import { ButtonGroup } from "@xola/ui-kit";
import { DateFormat, formatDate, JsDateRange, now } from "common";
import { Dayjs } from "dayjs";
import React, { useState } from "react";
import { useQuery } from "react-query";
import { ExportJob, PurchaseItemObject, PurchaseStatus } from "types";
import shallow from "zustand/shallow";
import { ExportJobModal } from "../../Common/ExportJobModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { ReportDatePicker } from "../ReportDatePicker";
import { api } from "@/api/api";
import { useModal } from "@/hooks/useVisibility";
import { getInitialDate } from "@/models/Reports";
import { GiftStoreProductType, useGiftFilterStore } from "@/stores/reports/giftsReportFilterStore";

export const GiftReportFilter = () => {
    const [from, to] = useGiftFilterStore((state) => [state.getFrom(), state.getTo()], shallow);
    const [setFrom, setTo] = useGiftFilterStore((state) => [state.setFrom, state.setTo], shallow);
    const [type, setType] = useGiftFilterStore((state) => [state.type, state.setType], shallow);

    const exportModal = useModal(false);
    const [value, setValue] = useState(getInitialDate(from, to));
    const handleDateChange = (dateValue: JsDateRange) => {
        if (dateValue.from && dateValue.to) {
            setFrom(now(dateValue.from));
            setTo(now(dateValue.to));
        }

        setValue(dateValue);
    };

    const activeIndex = type === GiftStoreProductType.ALL ? 0 : type === GiftStoreProductType.STORE_CREDIT ? 1 : 2;

    const handleTypeChange = (index: number) => {
        let type = GiftStoreProductType.ALL;
        if (index === 1) {
            type = GiftStoreProductType.STORE_CREDIT;
        } else if (index === 2) {
            type = GiftStoreProductType.GIFT;
        }

        setType(type);
    };

    return (
        <div className="flex w-full justify-between border-b border-gray-light px-6 py-3">
            <div className="flex max-w-xs space-x-4">
                <ReportDatePicker
                    isFutureDatesAllowed
                    value={value}
                    from={from}
                    to={to}
                    disabledDays={() => false} // No dates disabled
                    onChange={handleDateChange}
                />
                <div>
                    <ButtonGroup size="medium" value={activeIndex} onChange={handleTypeChange}>
                        <ButtonGroup.Button>All</ButtonGroup.Button>
                        <ButtonGroup.Button>Store Credits</ButtonGroup.Button>
                        <ButtonGroup.Button>Gifts</ButtonGroup.Button>
                    </ButtonGroup>
                </div>
            </div>

            <SecondaryButton icon={<ExportIcon />} onClick={exportModal.show}>
                Export
            </SecondaryButton>
            <GiftReportExportModal isOpen={exportModal.isVisible} onClose={exportModal.hide} />
        </div>
    );
};

interface GiftReportExportModalProps {
    isOpen: boolean;
    onClose: (e: any) => void;
}

const GiftReportExportModal = ({ isOpen, onClose }: GiftReportExportModalProps) => {
    const params: any = useGiftFilterStore((state) => getDateParams(state.getFrom(), state.getTo()));
    const type = useGiftFilterStore((state) => state.type);

    params.items_object =
        type === GiftStoreProductType.ALL
            ? [PurchaseItemObject.GIFT, PurchaseItemObject.STORE_CREDIT]
            : typeToItemObjectMap[type];

    params["status[notEqual]"] = PurchaseStatus.DRAFT;

    const { data: job } = useQuery<ExportJob>(
        ["analytics", "gifts", params],
        async () => {
            const { data } = await api.get<ExportJob>("/analytics/export/gifts", { params });
            return data;
        },
        { enabled: isOpen },
    );

    return <ExportJobModal isOpen={isOpen} job={job} onClose={onClose} />;
};

// This is added separately as for export createdAt[range] doesn't work from API, this needs to be fixed in API, for now fixing it here.
const getDateParams = (from: Dayjs, to: Dayjs) => {
    const params = [formatDate(from, DateFormat.DATE_TIME_ISO), formatDate(to, DateFormat.DATE_TIME_ISO)];
    return { createdAt: params.join(",") };
};

const typeToItemObjectMap: { [key: string]: string } = {
    [GiftStoreProductType.STORE_CREDIT]: PurchaseItemObject.STORE_CREDIT,
    [GiftStoreProductType.GIFT]: PurchaseItemObject.GIFT,
};
